import { Question } from './types';

export type GridSpan = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

const DEFAULT_GRID_SPAN: GridSpan = 3;

// Full-width below `md` so form questions stack on mobile instead of
// squeezing the 12-column grid into a phone viewport.
export const SPAN_CLASSES: Record<GridSpan, string> = {
  1: 'col-span-12 md:col-span-1',
  2: 'col-span-12 md:col-span-2',
  3: 'col-span-12 md:col-span-3',
  4: 'col-span-12 md:col-span-4',
  5: 'col-span-12 md:col-span-5',
  6: 'col-span-12 md:col-span-6',
  7: 'col-span-12 md:col-span-7',
  8: 'col-span-12 md:col-span-8',
  9: 'col-span-12 md:col-span-9',
  10: 'col-span-12 md:col-span-10',
  11: 'col-span-12 md:col-span-11',
  12: 'col-span-12',
};

/**
 * Returns the Tailwind col-span class for a child item relative to its parent container.
 * Exported for reuse across components that render question sub-grids.
 */
export const getSubgroupChildSpanClass = (
  childSpan: GridSpan,
  parentSpan: GridSpan
): string => {
  const cappedChild = Math.min(childSpan, parentSpan);
  const cols = Math.max(
    1,
    Math.min(12, Math.round((cappedChild * 12) / parentSpan))
  ) as GridSpan;
  return SPAN_CLASSES[cols];
};

/**
 * Heuristic fallback column span when no authoritative width is set.
 * Exported for testing.
 */
export const getQuestionSpan = (question: Question): GridSpan => {
  if (question.type === 'multiselect') {
    if (question.values && question.values.length > 8) return 12;
    if (question.values && question.values.length > 4) return 6;
    return DEFAULT_GRID_SPAN;
  }

  if (question.type === 'text') {
    const q = question.question.toLowerCase();
    if (
      q.includes('describe') ||
      q.includes('explain') ||
      q.includes('details') ||
      q.includes('comments')
    ) {
      return 12;
    }
    return DEFAULT_GRID_SPAN;
  }

  if (question.type === 'radio' && question.values) {
    if (question.values.length > 6) return 12;
    if (question.values.length > 3) return 6;
    return DEFAULT_GRID_SPAN;
  }

  return DEFAULT_GRID_SPAN;
};

/**
 * Resolves the effective grid span for a question using the following precedence:
 *  1. roger_emr_display_width from the question schema (authoritative)
 *  2. Heuristic based on question type/values
 * Exported for testing.
 */
export const resolveQuestionSpan = (question: Question): GridSpan => {
  if (
    question.roger_emr_display_width !== undefined &&
    question.roger_emr_display_width >= 1 &&
    question.roger_emr_display_width <= 12
  ) {
    return question.roger_emr_display_width as GridSpan;
  }
  return getQuestionSpan(question);
};

/** Inactivity window before pending visit form edits are autosaved. */
export const OASIS_AUTOSAVE_DEBOUNCE_MS = 2000;

/**
 * Single source of truth for the visit form autosave debounce policy.
 * Signed visits save immediately so the invalidation dialog appears at once.
 */
export const getOasisAutosaveDebounceMs = (
  hasActiveSignatures: boolean
): number => (hasActiveSignatures ? 0 : OASIS_AUTOSAVE_DEBOUNCE_MS);
